import {
  Avatar,
  Typography,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { IconWithDropdown } from "@/components/molecules";
import { Company } from "@/types/company";
import { Box, Tooltip } from "@/components/atoms";
import { JobPost } from "@/types/jobPost";
import { ApplicationStatus, SavedJobPost } from "@/types/user";

type ColumnKey<T> =
  | Extract<keyof T, string | number>
  | "actions"
  | "questionnaire";

interface Column<T> {
  key: ColumnKey<T>;
  label: string;
}

export interface TableProps<T> {
  componentName: string;
  title: string;
  columns: Column<T>[];
  rows: T[];
  onEdit?: (row: T) => void;
  onRemove?: (row: T) => void;
  onCompanyNameClick?: (row: T) => void;
  onJobPostClick?: (row: T) => void;
  onNoteClick?: (row: T) => void;
  onSavedReasonClick?: (row: T) => void;
  onApplicationStatusChange?: (row: T, newStatus: ApplicationStatus) => void;
  handleQuestionnaireClick?: (row: T) => void;
  isDisabled?: boolean;
  isQuestionnaireComplete?: (row: T) => boolean;
}

export const renderCellValue = (value: any): React.ReactNode => {
  if (Array.isArray(value)) return value.join(", ");
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined) return "-";
  return value.toString();
};

const getTruncatedText = (value: any, fallback = "Add text") => {
  return typeof value === "string" && value
    ? `${value.slice(0, 30)}${value.length > 30 ? "..." : ""}`
    : fallback;
};

const applicationStatusOptions: ApplicationStatus[] = [
  "saved",
  "applied",
  "interviewing",
  "rejected",
  "offer",
];

export const Table = <
  T extends {
    logo_url?: Company["logo_url"];
    company_name: Company["company_name"];
    id: Company["id"];
    job_title?: JobPost["job_title"];
    application_status_comment?: SavedJobPost["application_status_comment"];
    number_of_experienced_years?: SavedJobPost["number_of_experienced_years"];
    saved_reason?: SavedJobPost["saved_reason"];
  },
>({
  componentName,
  title,
  columns,
  rows,
  onEdit,
  onRemove,
  onCompanyNameClick,
  onJobPostClick,
  onNoteClick,
  onSavedReasonClick,
  onApplicationStatusChange,
  handleQuestionnaireClick,
  isDisabled,
  isQuestionnaireComplete,
}: TableProps<T>) => {
  const content = (
    <Box
      componentName={`${componentName}-table`}
      border={false}
      sx={{
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <TableContainer component={Paper}>
        <MuiTable size="small">
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={String(col.key)}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No {title.toLowerCase()}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index}>
                  {columns.map(col => {
                    const value = row[col.key as keyof T];
                    const isCompanyClickable =
                      col.key === "company_name" &&
                      typeof value === "string" &&
                      onCompanyNameClick;
                    const isJobClickable =
                      col.key === "job_title" &&
                      typeof value === "string" &&
                      onJobPostClick;

                    const isNoteCell = col.key === "notes";
                    const isReasonCell = col.key === "saved_reason";
                    const isQuestionnaireCell = col.key === "questionnaire";

                    return (
                      <TableCell key={String(col.key)}>
                        {col.key === "logo_url" ? (
                          <Avatar
                            src={row.logo_url ?? undefined}
                            alt={row.company_name}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          />
                        ) : col.key === "actions" ? (
                          <IconWithDropdown
                            onEdit={() => onEdit?.(row)}
                            onRemove={() => onRemove?.(row)}
                          />
                        ) : isCompanyClickable ? (
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{
                              cursor: "pointer",
                              fontWeight: "bold",
                              width: "fit-content",
                            }}
                            onClick={() => onCompanyNameClick?.(row)}
                          >
                            {value}
                          </Typography>
                        ) : isJobClickable ? (
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{
                              cursor: "pointer",
                              fontWeight: "bold",
                              width: "fit-content",
                            }}
                            onClick={() => onJobPostClick?.(row)}
                          >
                            {value}
                          </Typography>
                        ) : isNoteCell ? (
                          <Typography
                            variant="body2"
                            color={value ? "text.primary" : "text.secondary"}
                            fontStyle={value ? "normal" : "italic"}
                            sx={{ cursor: "pointer" }}
                            onClick={() => onNoteClick?.(row)}
                          >
                            {getTruncatedText(value, "Add note")}
                          </Typography>
                        ) : isReasonCell ? (
                          <Typography
                            variant="body2"
                            color={value ? "text.primary" : "text.secondary"}
                            fontStyle={value ? "normal" : "italic"}
                            sx={{ cursor: "pointer" }}
                            onClick={() => onSavedReasonClick?.(row)}
                          >
                            {getTruncatedText(value, "Add Saved Reason")}
                          </Typography>
                        ) : col.key === "application_status" ? (
                          <select
                            value={(value as ApplicationStatus) || ""}
                            onChange={e =>
                              onApplicationStatusChange?.(
                                row,
                                e.target.value as ApplicationStatus,
                              )
                            }
                            style={{
                              padding: "4px 8px",
                              borderRadius: 4,
                              border: "1px solid #ccc",
                              backgroundColor: "#fff",
                            }}
                          >
                            {applicationStatusOptions.map(status => (
                              <option key={status} value={status}>
                                {status.charAt(0).toUpperCase() +
                                  status.slice(1)}
                              </option>
                            ))}
                          </select>
                        ) : isQuestionnaireCell &&
                          handleQuestionnaireClick &&
                          isQuestionnaireComplete ? (
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ cursor: "pointer", fontWeight: "bold" }}
                            onClick={() => handleQuestionnaireClick(row)}
                          >
                            {isQuestionnaireComplete(row)
                              ? "Completed"
                              : "Incomplete"}
                          </Typography>
                        ) : (
                          renderCellValue(value)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
  return isDisabled ? (
    <Tooltip
      componentName={`${componentName}-table`}
      tooltipMessage="Upgrade your account to access this feature"
      readMoreLink="/memberships"
    >
      <span>{content}</span>
    </Tooltip>
  ) : (
    content
  );
};
